import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import MagicButton from 'components/MagicButton/MagicButton';
import SmsConsentDisclosure from 'components/SmsConsentDisclosure/SmsConsentDisclosure';
import StatusMessage from 'components/StatusMessage/StatusMessage';
import { AssetUrls } from 'config/assets';
import { showSuccess } from 'utils/toastNotifications';
import {
  useSendVerificationCodeMutation,
  useVerifyPhoneMutation,
  useGetPhoneVerificationStatusQuery
} from 'store/services/auth/authApi';
import { loginSuccess } from 'store/services/auth/authSlice';
import { RootState } from 'store';
import './MobileVerificationModal.css';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  currentPhone?: string | null;
  onVerificationSuccess?: () => void;
}

const MobileVerificationModal: React.FC<Props> = ({
  isOpen,
  onClose,
  currentPhone,
  onVerificationSuccess
}) => {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);

  const [step, setStep] = useState<'phone' | 'verify'>('phone');
  const [phoneValue, setPhoneValue] = useState<string | undefined>('');
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [canResend, setCanResend] = useState(true);
  const [resendTimer, setResendTimer] = useState(0);
  const [verificationSid, setVerificationSid] = useState('');

  const codeInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // API hooks
  const [sendCode, { isLoading: isSending, error: sendError, reset: resetSendError }] = useSendVerificationCodeMutation();
  const [verifyPhone, { isLoading: isVerifying, error: verifyError, reset: resetVerifyError }] = useVerifyPhoneMutation();
  const { refetch: refetchPhoneStatus } = useGetPhoneVerificationStatusQuery();

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setStep('phone');
      setPhoneValue('');
      setVerificationCode(['', '', '', '', '', '']);
      setCanResend(true);
      setResendTimer(0);
      setVerificationSid('');
      resetSendError();
      resetVerifyError();
    }
  }, [isOpen, resetSendError, resetVerifyError]);

  // Handle resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => {
        setResendTimer(resendTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [resendTimer]);

  const isPhoneValid = () => {
    // PhoneInput returns E.164 format (e.g., "+14155551234")
    // A valid phone number should have at least country code + 7 digits
    return phoneValue && phoneValue.length >= 8; // +1 + at least 7 digits
  };

  const handleSendCode = async () => {
    if (!phoneValue) return;

    // Parse E.164 format phone number (e.g., "+14155551234")
    // Extract country code and phone number
    const matches = phoneValue.match(/^(\+\d{1,4})(\d+)$/);
    if (!matches) {
      console.error('Invalid phone number format');
      return;
    }

    const countryCode = matches[1]; // e.g., "+1"
    const digitsOnly = matches[2];  // e.g., "4155551234"

    try {
      const result = await sendCode({
        phoneNumber: digitsOnly,
        countryCode: countryCode
      }).unwrap();

      setVerificationSid(result.verificationSid);
      setStep('verify');
      setCanResend(false);
      setResendTimer(60); // 60 second cooldown

      // Focus first code input
      setTimeout(() => {
        codeInputRefs.current[0]?.focus();
      }, 100);
    } catch (err: any) {
      console.error('Failed to send verification code:', err);
    }
  };

  const handleResendCode = async () => {
    if (!canResend) return;

    // Clear the verification code inputs
    setVerificationCode(['', '', '', '', '', '']);

    // Reset focus to first input
    codeInputRefs.current[0]?.focus();

    // Send new code
    await handleSendCode();
  };

  const handleCodeChange = (index: number, value: string) => {
    // Only allow digits
    const digit = value.replace(/\D/g, '').slice(0, 1);

    const newCode = [...verificationCode];
    newCode[index] = digit;
    setVerificationCode(newCode);

    // Auto-focus next input
    if (digit && index < 5) {
      codeInputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newCode = [...verificationCode];

    for (let i = 0; i < pastedText.length && i < 6; i++) {
      newCode[i] = pastedText[i];
    }

    setVerificationCode(newCode);

    // Focus the next empty input or the last one
    const nextEmptyIndex = newCode.findIndex(digit => !digit);
    if (nextEmptyIndex !== -1) {
      codeInputRefs.current[nextEmptyIndex]?.focus();
    } else {
      codeInputRefs.current[5]?.focus();
    }
  };

  const isCodeComplete = () => {
    return verificationCode.every(digit => digit !== '');
  };

  const handleVerifyCode = async () => {
    const code = verificationCode.join('');
    if (code.length !== 6 || !phoneValue) return;

    // Parse E.164 format to extract digits only
    const matches = phoneValue.match(/^(\+\d{1,4})(\d+)$/);
    if (!matches) {
      console.error('Invalid phone number format');
      return;
    }
    const digitsOnly = matches[2];

    try {
      const result = await verifyPhone({
        phoneNumber: digitsOnly, // Must match what was sent to send-verification-code
        code,
        verificationSid
      }).unwrap();

      // Update tokens using secure auth flow
      if (result.token || result.csrfToken) {
        dispatch(loginSuccess({
          token: result.token || '',
          csrfToken: result.csrfToken,
          user: user || undefined // Use existing user from Redux state
        }));
      }

      // Show success message
      showSuccess(currentPhone ? 'Phone number updated successfully!' : 'Phone verified successfully!');

      // Refresh phone status
      await refetchPhoneStatus();

      // Call success callback
      if (onVerificationSuccess) {
        onVerificationSuccess();
      }

      // Close modal after short delay
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err: any) {
      console.error('Failed to verify code:', err);
    }
  };

  const getErrorMessage = (error: any) => {
    if (!error?.data) return 'An error occurred. Please try again.';

    const message = error.data.message || error.data.error;

    // Check for specific error types
    if (message?.includes('already registered')) {
      return 'This phone number is already registered to another account.';
    }
    if (message?.includes('not allowed') || message?.includes('blocked')) {
      return 'This phone number type is not allowed. Please use a valid mobile number (Google Voice and VOIP numbers are not accepted).';
    }
    if (message?.includes('rate limit') || error.status === 429) {
      return 'Too many attempts. Please try again later (max 10 attempts per hour).';
    }
    if (message?.includes('Invalid') || error.status === 400) {
      return 'Invalid verification code. Please check and try again.';
    }
    if (message?.includes('expired') || error.status === 404) {
      return 'Verification code has expired. Please request a new one.';
    }

    return message || 'An error occurred. Please try again.';
  };

  const handleBackToPhone = () => {
    setStep('phone');
    setVerificationCode(['', '', '', '', '', '']);
    resetVerifyError();
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="mobile-verification-modal-overlay" onClick={onClose}>
      <div className="mobile-verification-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mobile-verification-modal-header">
          <h2 className="mobile-verification-modal-title">
            {currentPhone ? 'Change Phone Number' : 'Verify Phone Number'}
          </h2>
          <button className="mobile-verification-close-button" onClick={onClose}>
            <img
              src={AssetUrls.icons.dropdowns.close}
              alt="Close"
              style={{ width: 16, height: 16 }}
            />
          </button>
        </div>

        <div className="mobile-verification-modal-body">
          {step === 'phone' ? (
            <>
              <p className="mobile-verification-description">
                {currentPhone
                  ? 'Enter a new phone number to update your account.'
                  : 'Verify to unlock all features and receive 100 bonus points.'}
              </p>

              <div className="phone-input-wrapper">
                <PhoneInput
                  international
                  defaultCountry="US"
                  value={phoneValue}
                  onChange={setPhoneValue}
                  className="react-phone-input"
                  placeholder="Enter phone number"
                />
              </div>

              {sendError && (
                <StatusMessage
                  type="error"
                  message={getErrorMessage(sendError)}
                />
              )}

              <SmsConsentDisclosure />

              <div className="mobile-verification-modal-footer">
                <MagicButton
                  label="Cancel"
                  color="dark-grey"
                  onPress={onClose}
                  isDisabled={isSending}
                />
                <MagicButton
                  label="Send Code"
                  color="purple"
                  onPress={handleSendCode}
                  isDisabled={!isPhoneValid() || isSending}
                />
              </div>
            </>
          ) : (
            <>
              <div className="verification-code-section">
                <p className="verification-code-label">Enter the verification code</p>
                <p className="verification-code-sublabel">
                  We sent a 6-digit code to {phoneValue}
                </p>

                <div className="verification-code-inputs">
                  {verificationCode.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => {
                        codeInputRefs.current[index] = el;
                      }}
                      type="text"
                      inputMode="numeric"
                      className="code-input"
                      value={digit}
                      onChange={(e) => handleCodeChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      onPaste={index === 0 ? handlePaste : undefined}
                      maxLength={1}
                      autoFocus={index === 0}
                    />
                  ))}
                </div>

                {resendTimer > 0 && (
                  <p className="resend-timer">
                    Resend code in {resendTimer} seconds
                  </p>
                )}

                {canResend && (
                  <button
                    className="resend-button"
                    onClick={handleResendCode}
                    disabled={isSending}
                  >
                    Resend code
                  </button>
                )}

                <button
                  className="back-to-phone-button"
                  onClick={handleBackToPhone}
                >
                  Change phone number
                </button>
              </div>

              {verifyError && (
                <StatusMessage
                  type="error"
                  message={getErrorMessage(verifyError)}
                />
              )}

              <div className="mobile-verification-modal-footer">
                <MagicButton
                  label="Cancel"
                  color="dark-grey"
                  onPress={onClose}
                  isDisabled={isVerifying}
                />
                <MagicButton
                  label="Verify"
                  color="purple"
                  onPress={handleVerifyCode}
                  isDisabled={!isCodeComplete() || isVerifying}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default MobileVerificationModal;